/**
 * Register service. See frontend spec §5.3.
 *
 * Loads groups via `GET /api/v1/admin/groups` (read-only, already in
 * scope), then `POST /api/v1/admin/services` on submit. On success, shows
 * the one-time `apiKey` via `SecretRevealOnce` - the plaintext key is never
 * returned again after this response, per spec.
 *
 * No fake fields: only name and groupIds - nothing else the
 * backend doesn't accept.
 */
import { showToast } from "../components/toast";
import { HELP, renderInfoTooltip } from "../components/tooltip";
import {
  filterAccessGroups,
  renderAccessGroupPicker,
  syncAccessGroupPickerUi,
} from "../components/accessGroupPicker";
import { createService, listGroups } from "../services/admin";
import { ApiError } from "../services/apiClient";
import type { AuthGroup, ServiceAccountCreatedData } from "../types/api";
import { el, mount, type ElChild } from "../utils/dom";
import { sortAccessGroups } from "../utils/format";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState } from "../components/states";
import { renderSecretReveal } from "../components/secretReveal";
import { navigateBack } from "../app/navigation";

export class ServiceCreatePage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading groups…")]);
  private navigate: (path: string) => void = () => undefined;
  private selectedGroupIds = new Set<number>();
  private submitting = false;
  private groups: AuthGroup[] = [];
  private nameValue = "";
  private groupQuery = "";

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    this.navigate = navigate;

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Register service",
          subtitle: "Register a service and choose which access groups it should use.",
          back: { fallbackPath: "/services", navigate },
        }),
        this.body,
      ])
    );

    void this.load();
  }

  destroy(): void {
    // No interval - single-shot form.
  }

  private async load(): Promise<void> {
    try {
      this.groups = sortAccessGroups(await listGroups());
      this.renderForm();
    } catch (err) {
      mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
    }
  }

  private renderForm(): void {
    const nameInput = el("input", {
      className: "input input--mono",
      type: "text",
      placeholder: "billing-service",
      "aria-label": "Name",
      value: this.nameValue,
      oninput: (ev: Event) => (this.nameValue = (ev.target as HTMLInputElement).value),
    }) as HTMLInputElement;

    const submitButton = el(
      "button",
      {
        className: "btn btn-primary btn-sm",
        type: "button",
        disabled: this.submitting,
        onclick: () => void this.submit(),
      },
      [this.submitting ? "Registering…" : "Register service"]
    );

    const cancelButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        onclick: () => navigateBack(this.navigate, "/services"),
      },
      ["Cancel"]
    );

    mount(
      this.body,
      el("div", { className: "panel panel--standard" }, [
        field("Name", nameInput),
        field(this.accessGroupsLabel(), this.renderGroupsPicker()),
        el("div", { className: "form-actions form-actions--mt-8" }, [
          submitButton,
          cancelButton,
        ]),
      ])
    );
  }

  private accessGroupsLabel(): HTMLElement {
    return el("span", { className: "label-with-help" }, [
      el("span", {}, [
        "Access groups (",
        el("span", { dataset: { selectedCount: "" } }, [String(this.selectedGroupIds.size)]),
        " selected)",
      ]),
      renderInfoTooltip(HELP.accessGroup),
    ]);
  }

  private renderGroupsPicker(): HTMLElement {
    return renderAccessGroupPicker({
      groups: this.groups,
      selectedIds: this.selectedGroupIds,
      query: this.groupQuery,
      disabled: this.submitting,
      onQueryChange: (query) => {
        this.groupQuery = query;
      },
      onToggle: (groupId) => {
        if (this.selectedGroupIds.has(groupId)) this.selectedGroupIds.delete(groupId);
        else this.selectedGroupIds.add(groupId);
        this.syncSelectionChrome();
      },
      onToggleVisible: (select) => {
        const visible = filterAccessGroups(this.groups, this.groupQuery);
        for (const group of visible) {
          if (select) this.selectedGroupIds.add(group.id);
          else this.selectedGroupIds.delete(group.id);
        }
        this.syncSelectionChrome();
      },
    });
  }

  private syncSelectionChrome(): void {
    syncAccessGroupPickerUi(this.body, this.groups, this.selectedGroupIds, this.groupQuery);
    const count = this.body.querySelector("[data-selected-count]");
    if (count) count.textContent = String(this.selectedGroupIds.size);
  }

  private async submit(): Promise<void> {
    const trimmedName = this.nameValue.trim();

    if (!trimmedName) {
      showToast("Name is required.", "error");
      return;
    }
    if (this.selectedGroupIds.size === 0) {
      showToast("Select at least one group so this service can read data.", "error");
      return;
    }

    this.submitting = true;
    this.renderForm();

    try {
      const created = await createService({
        name: trimmedName,
        groupIds: Array.from(this.selectedGroupIds),
      });
      showToast("Service registered.", "success");
      this.renderSuccess(created);
    } catch (err) {
      showToast(errorMessage(err), "error");
      this.submitting = false;
      this.renderForm();
    }
  }

  private renderSuccess(created: ServiceAccountCreatedData): void {
    mount(
      this.body,
      el("div", {}, [
        renderSecretReveal(created.apiKey, `API key for "${created.name}"`),
        el("div", { className: "form-actions form-actions--mt-6" }, [
          el(
            "button",
            {
              className: "btn btn-primary btn-sm",
              type: "button",
              onclick: () => this.navigate(`/services/${created.id}`),
            },
            ["View Service"]
          ),
          el(
            "button",
            {
              className: "btn btn-ghost btn-sm",
              type: "button",
              onclick: () => navigateBack(this.navigate, "/services"),
            },
            ["Back to list"]
          ),
        ]),
      ])
    );
  }
}

function field(label: ElChild, control: HTMLElement): HTMLElement {
  return el("div", { className: "field field--spaced" }, [
    el("span", { className: "field__label" }, [label]),
    control,
  ]);
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message;
  }
  return "Something went wrong.";
}
